import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

/* -------------------------------------------------------------------------- */

interface VipStore {
	vip_username: string | null;
	vip_profilePicture: string | null;
	vip_level: number | null;
	vip_vipStatus: string | null;
	vip_amountWagered: number | null;

	vip_progress: {
		levelStart: number;
		currentXp: number;
		nextLevelXp: number;
	};

	vip_totalRewardsClaimed: number;
	vip_totalEveryHeld: number;

	vip_rewards: {
		id: string;
		title: string;
		expiresAt: string;
		status: string;
		image_url: string;
	}[];

	vip_rakeback: {
		id: string;
		claimAmount: number;
		progress: number;
	};

	vip_weeklyReward: {
		id: string;
		claimAmount: number;
		progress: number;
		claims: {
			weekly: {
				id: string;
				claimAmount: number;
			};
			freeSpins: {
				id: string;
				claimAmount: number;
			};
		};
	};

	actions: {
		vip_resetVipStore: () => void;
		setVip_username: (val: VipStore['vip_username']) => void;
		setVip_profilePicture: (val: VipStore['vip_profilePicture']) => void;
		setVip_level: (val: VipStore['vip_level']) => void;
		setVip_vipStatus: (val: VipStore['vip_vipStatus']) => void;
		setVip_amountWagered: (val: VipStore['vip_amountWagered']) => void;
		setVip_progress: (val: VipStore['vip_progress']) => void;
		setVip_totalRewardsClaimed: (
			val: VipStore['vip_totalRewardsClaimed']
		) => void;
		setVip_totalEveryHeld: (val: VipStore['vip_totalEveryHeld']) => void;
		setVip_rewards: (val: VipStore['vip_rewards']) => void;
		setVip_rakeback: (val: VipStore['vip_rakeback']) => void;
		setVip_weeklyReward: (val: VipStore['vip_weeklyReward']) => void;
		setVip_weeklyRewardClaims: (
			val: VipStore['vip_weeklyReward']['claims']
		) => void;
	};
}

/* -------------------------------------------------------------------------- */

const initialState: Omit<VipStore, 'actions'> = {
	vip_username: null,
	vip_profilePicture: null,
	vip_level: null,
	vip_vipStatus: null,
	vip_amountWagered: null,
	vip_progress: {
		levelStart: 0,
		currentXp: 0,
		nextLevelXp: 0,
	},
	vip_totalRewardsClaimed: 0,
	vip_totalEveryHeld: 0,
	vip_rewards: [],
	vip_rakeback: {
		id: '',
		claimAmount: 0,
		progress: 0,
	},
	vip_weeklyReward: {
		id: '',
		claimAmount: 0,
		progress: 0,
		claims: {
			weekly: {
				id: '',
				claimAmount: 0,
			},
			freeSpins: {
				id: '',
				claimAmount: 0,
			},
		},
	},
};

/* -------------------------------------------------------------------------- */

const vipStore = create<VipStore>((set) => ({
	...initialState,

	actions: {
		vip_resetVipStore: () => set(() => ({ ...initialState })),

		setVip_username: (val) => set(() => ({ vip_username: val })),
		setVip_profilePicture: (val) => set(() => ({ vip_profilePicture: val })),
		setVip_level: (val) => set(() => ({ vip_level: val })),
		setVip_vipStatus: (val) => set(() => ({ vip_vipStatus: val })),
		setVip_amountWagered: (val) => set(() => ({ vip_amountWagered: val })),

		setVip_progress: (val) => set(() => ({ vip_progress: val })),
		setVip_totalRewardsClaimed: (val) =>
			set(() => ({ vip_totalRewardsClaimed: val })),
		setVip_totalEveryHeld: (val) => set(() => ({ vip_totalEveryHeld: val })),

		setVip_rewards: (val) => set(() => ({ vip_rewards: val })),
		setVip_rakeback: (val) => set(() => ({ vip_rakeback: val })),
		setVip_weeklyReward: (val) => set(() => ({ vip_weeklyReward: val })),

		setVip_weeklyRewardClaims: (val) =>
			set((state) => ({
				vip_weeklyReward: {
					...state.vip_weeklyReward,
					claims: val,
				},
			})),
	},
}));

/* -------------------------------------------------------------------------- */
/* ----------------------------- state selectors ---------------------------- */

export const useVip_username = () => vipStore((state) => state.vip_username);
export const useVip_profilePicture = () =>
	vipStore((state) => state.vip_profilePicture);
export const useVip_level = () => vipStore((state) => state.vip_level);
export const useVip_vipStatus = () => vipStore((state) => state.vip_vipStatus);
export const useVip_amountWagered = () =>
	vipStore((state) => state.vip_amountWagered);

export const useVip_progress = () =>
	vipStore(useShallow((state) => state.vip_progress));
export const useVip_totalRewardsClaimed = () =>
	vipStore((state) => state.vip_totalRewardsClaimed);
export const useVip_totalEveryHeld = () =>
	vipStore((state) => state.vip_totalEveryHeld);

export const useVip_rewards = () => vipStore((state) => state.vip_rewards);
export const useVip_rakeback = () =>
	vipStore(useShallow((state) => state.vip_rakeback));
export const useVip_weeklyReward = () =>
	vipStore(useShallow((state) => state.vip_weeklyReward));
export const useVip_weeklyRewardClaims = () =>
	vipStore(useShallow((state) => state.vip_weeklyReward.claims));

/* -------------------------------------------------------------------------- */

export const useVip_actions = () => vipStore((state) => state.actions);
